import type { Project } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { Play, RotateCw, Square } from "lucide-react";
import { createContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { projectsApi } from "@/api/projects.ts";
import { teamApi } from "@/api/studio.ts";
import { Folder } from "lucide-react";
import { Dock, DOCK_TENANTS, type DockTenant } from "@/features/dock/Dock.tsx";
import { Alert, Button, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { DockerBanner } from "./ProjectsPage.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { WorkspaceActions } from "./WorkspaceActions.tsx";
import { useOpenIde } from "./useOpenIde.ts";

export const projectQuery = (id: string) => ({ queryKey: ["projects", id] as const, queryFn: () => projectsApi.get(id) });

export function useProjectActions(id: string) {
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const { t } = useTranslation();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["projects"] });
    void qc.invalidateQueries({ queryKey: ["projects", id, "container"] });
  };
  const mk = (fn: (id: string) => Promise<Project>) =>
    useMutation({ mutationFn: () => fn(id), onSuccess: (p) => { qc.setQueryData(["projects", id], p); invalidate(); }, onError: invalidate });
  // Anything that interrupts the container asks first; start is harmless.
  const guarded = (m: ReturnType<typeof mk>, key: "stop" | "restart" | "recreate") => ({
    ...m,
    mutate: () => { void confirm({ title: t(`confirm.${key}Title`), message: t(`confirm.${key}Msg`), confirmLabel: t(`projects.${key}`), danger: key !== "restart" }).then((ok) => ok && m.mutate()); },
  });
  return { start: mk(projectsApi.start), stop: guarded(mk(projectsApi.stop), "stop"), restart: guarded(mk(projectsApi.restart), "restart"), recreate: guarded(mk(projectsApi.recreate), "recreate") };
}

/** The chat page renders the project's tool group inside its own header. */
export const ChatHeaderToolsContext = createContext<React.ReactNode>(null);

/** Pending element-picker captures are handed to whichever chat composer is mounted. */
export const captureBus = new EventTarget();

/** Project layout: slim header with lifecycle + workspace actions, sub-navigation, content, and the tool dock. */
/** `personal`: the Chat section's own space; always laid out as a chat (tools and dock), started automatically. */
export function ProjectPage({ projectId, personal = false }: { projectId: string; personal?: boolean }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { dock?: string };
  const dock = DOCK_TENANTS.includes(search.dock as DockTenant) ? (search.dock as DockTenant) : null;
  const q = useQuery({ ...projectQuery(projectId), refetchInterval: (query) => (query.state.data?.state === "creating" ? 1500 : false) });
  const actions = useProjectActions(projectId);
  const [showNav, setShowNav] = useState(true);
  const p = q.data;
  const pathname = useLocation({ select: (l) => l.pathname });
  const inChat = personal || pathname.includes("/chat/");
  useEffect(() => setShowNav(!inChat), [inChat]);
  const team = useQuery({ queryKey: ["mentionables", projectId], queryFn: () => teamApi.mentionables(projectId), staleTime: 30_000 });
  const ide = useOpenIde(projectId);
  // the personal space has no start button: bring it up whenever it is found stopped
  const autoStart = actions.start;
  useEffect(() => {
    if (personal && p && (p.state === "stopped" || p.state === "none") && !autoStart.isPending) autoStart.mutate();
  }, [personal, p?.state]);
  if (!p) return q.isError ? <div className="p-6"><Alert tone="danger">{toMsg(q.error)}</Alert></div> : <div className="grid h-full place-items-center"><Spinner /></div>;

  const busy = actions.start.isPending || actions.stop.isPending || actions.restart.isPending || actions.recreate.isPending || p.state === "creating";
  const canEdit = p.myRole !== "viewer";
  const err = actions.start.error ?? actions.stop.error ?? actions.restart.error ?? actions.recreate.error;
  const setDock = (tnt: DockTenant | null) => void nav({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, dock: tnt ?? undefined }) as never, replace: true });
  const tabs = [
    { to: "/projects/$projectId", label: t("projects.tabs.overview"), exact: true },
    { to: "/projects/$projectId/chat", label: t("projects.tabs.chat") },
    { to: "/projects/$projectId/settings", label: t("projects.tabs.settings") },
  ];

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {!inChat && <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          {inChat && <Link to="/projects/$projectId" params={{ projectId }} className="grid size-9 place-items-center rounded-lg border border-border text-fg-muted hover:text-fg" aria-label={p.name}><Folder className="size-4" /></Link>}
          <div className="min-w-0">
            <Link to="/projects/$projectId" params={{ projectId }} className="block truncate font-semibold">{p.name}</Link>
            <div dir="ltr" className="hidden truncate font-mono text-[11px] text-fg-muted sm:block">/workspace · {p.slug}</div>
          </div>
          <StateBadge state={p.state} />
          <div className="ms-auto flex items-center gap-1">
            {canEdit && (p.state === "running" ? (
              <>
                <Button size="sm" variant="ghost" loading={actions.restart.isPending} disabled={busy} onClick={() => actions.restart.mutate()} title={t("projects.restart")} aria-label={t("projects.restart")}><RotateCw className="size-4" /></Button>
                <Button size="sm" variant="ghost" loading={actions.stop.isPending} disabled={busy} onClick={() => actions.stop.mutate()} title={t("projects.stop")} aria-label={t("projects.stop")}><Square className="size-4" /></Button>
              </>
            ) : (
              <Button size="sm" loading={actions.start.isPending || p.state === "creating"} disabled={busy} onClick={() => actions.start.mutate()}><Play className="size-4" /> {t("projects.start")}</Button>
            ))}
            {inChat && <WorkspaceActions hideTeam={personal} active={dock} compact={!!dock} teamCount={personal ? undefined : team.data?.filter((m) => m.kind === "agent").length} onOpen={(tnt) => setDock(dock === tnt ? null : tnt)} onIde={p.state === "running" && canEdit ? () => ide.mutate() : undefined} />}
          </div>
          {showNav && (
            <nav className="-mb-2 flex w-full gap-1 overflow-x-auto pt-1">
              {tabs.map((tab) => (
                <Link key={tab.to} to={tab.to} params={{ projectId }} search={(prev: Record<string, unknown>) => prev as never} activeOptions={{ exact: !!tab.exact }} className="-mb-px whitespace-nowrap border-b-2 border-transparent px-2 py-1.5 text-xs text-fg-muted hover:text-fg" activeProps={{ className: "border-accent text-fg font-medium" }}>
                  {tab.label}
                </Link>
              ))}
            </nav>
          )}
        </header>}
        {(err || (p.state === "error" && p.stateMessage) || p.stateMessage?.endsWith("pending_restart")) && (
          <div className="space-y-2 px-4 pt-3">
            {err && <Alert tone="danger">{toMsg(err)}</Alert>}
            {p.state === "error" && p.stateMessage && <Alert tone="danger"><span dir="ltr" className="block text-start font-mono text-xs">{p.stateMessage}</span></Alert>}
            {p.stateMessage?.endsWith("pending_restart") && p.state !== "error" && (
              <Alert tone="warn" className="flex items-center justify-between gap-3">
                <span>{t("projects.pendingRestart")}</span>
                {canEdit && <Button size="sm" variant="secondary" loading={actions.restart.isPending} onClick={() => actions.restart.mutate()}>{t("projects.restart")}</Button>}
              </Alert>
            )}
          </div>
        )}
        <div className="px-4 pt-3"><DockerBanner /></div>
        {inChat ? (
          <ChatHeaderToolsContext.Provider value={<WorkspaceActions hideTeam={personal} active={dock} compact={false} teamCount={personal ? undefined : team.data?.filter((m) => m.kind === "agent").length} onOpen={(tnt) => setDock(dock === tnt ? null : tnt)} onIde={p.state === "running" && canEdit ? () => ide.mutate() : undefined} />}>
            <div className="min-h-0 flex-1"><Outlet /></div>
          </ChatHeaderToolsContext.Provider>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-3">
            <Outlet />
          </div>
        )}
      </div>
      {dock && <Dock projectId={projectId} tenant={dock} canEdit={canEdit} running={p.state === "running"} onClose={() => setDock(null)} onCapture={(block) => captureBus.dispatchEvent(new CustomEvent("capture", { detail: block }))} />}
    </div>
  );
}
